import { AxiosError } from 'axios'
import uniq from 'lodash/uniq'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { func, pattern, required, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import AdvisorSelectViewModel from 'client-user-management/components/AdvisorSelect/AdvisorSelectViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import { CancellationToken } from 'infra/cancellation'
import ModalViewModel from 'infra/ModalViewModel'
import { whileActive } from 'utils/rx-util'
import { Id } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import { pluralize } from 'utils/pluralization-util'
import MemberStore from 'iam/MemberStore'
import { UserDataType } from 'iam/models/UserDataType'
import { Portal as DtoPortal, UserSource } from 'iam/dtos/member'
import { match } from 'utils/pattern-match-util'
import { CustomerStore } from 'iam/CustomerStore'
import { ParsedCustomerRow, parseCsvFile } from 'utils/csv-parser'
import {
  bulkCreateCustomers,
  BulkSubmissionResult,
} from 'utils/bulk-create-util'
import { extractMessageFromError } from 'utils/error-util'

interface InviteResults {
  email: string
  success?: boolean
  error?: AxiosError
}

/**
 * Sign-in type selection.
 */
export type SignInType = 'UsernamePassword' | 'AccessLink'

export class ClientInvitationViewModel extends ModalViewModel {
  /**
   * The current content of the email address text area (for Username & Password mode).
   */
  @observable
  emailAddressTextArea = ''

  /**
   * The currently selected sign-in type.
   */
  @observable
  signInType: SignInType = 'UsernamePassword'

  /**
   * Customer given name (first name).
   */
  @observable
  customerGivenName = ''

  /**
   * Customer family name (last name).
   */
  @observable
  customerFamilyName = ''

  /**
   * Customer phone number.
   */
  @observable
  customerPhoneNumber = ''

  /**
   * Customer email address.
   */
  @observable
  customerEmail = ''

  /**
   * Whether bulk upload mode is active for customer invitations.
   */
  @observable
  bulkUploadMode = false

  /**
   * Parsed customer data from CSV upload.
   */
  @observable
  bulkCustomers: ParsedCustomerRow[] = []

  /**
   * Error message from CSV parsing (header errors, etc.).
   */
  @observable
  bulkUploadError = ''

  /**
   * Name of the uploaded CSV file.
   */
  @observable
  bulkUploadFileName = ''

  /**
   * Results from the last bulk submission (for showing results view).
   */
  @observable
  bulkSubmissionResults: BulkSubmissionResult[] = []

  /**
   * Whether to show the results view after bulk submission.
   */
  @observable
  showBulkResults = false

  /**
   * Advisor picker. Pages members who can be assigned.
   */
  readonly advisorSelect = new AdvisorSelectViewModel(
    (query, cancellationToken) => this.searchAdvisors(query, cancellationToken),
  )

  /**
   * Validation rules for regular workspace invitations.
   */
  invitationValidation = validationContext(this, {
    emailAddressTextArea: [
      func(
        () => this.emailAddresses.length > 0,
        'You must enter at least one valid email address.',
      ),
    ],
  })

  /**
   * Validation rules for customer invitations.
   */
  customerValidation = validationContext(this, {
    customerEmail: [
      required(),
      pattern('email'),
      func(({ value }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
      func(
        () => this.customerEmail.trim().length > 0,
        'You must enter a valid email address',
      ),
    ],
    customerGivenName: [
      func(
        () => this.customerGivenName.trim().length > 0,
        'You must enter a first name',
      ),
    ],
    customerFamilyName: [
      func(
        () => this.customerFamilyName.trim().length > 0,
        'You must enter a last name',
      ),
    ],
    customerPhoneNumber: [
      func(
        (ctx) =>
          // Only run the phone number rule if we have a value,
          // as phone numbers are optional.
          !ctx.value ||
          pattern(
            // Super basic regex for phone number chars.
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  constructor(
    private getCurrentMember: GetCurrentMember,
    private memberStore: MemberStore,
    private customerStore: CustomerStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
    this.advisorSelect.query$
      .pipe(
        whileActive(
          toStream(() => this.showing && this.canChooseAdvisor, true),
        ),
      )
      .subscribe()
  }

  /**
   * The current workspace.
   */
  @computed
  get workspace() {
    return this.getCurrentMember().workspace
  }

  /**
   * A list of valid email addresses extracted from email address text area.
   */
  @computed
  get emailAddresses() {
    return uniq(
      this.emailAddressTextArea
        .split(/[,|\n]/)
        .map((e) => e.trim().toLowerCase())
        .filter(
          (e) =>
            !!e &&
            validationContext(
              { email: e },
              { email: [pattern('email')] },
            ).validate().isValid,
        ),
    )
  }

  /**
   * Whether to use access link sign-in type.
   */
  @computed
  get canUseAccessLink() {
    const hasPermission =
      this.getCurrentMember().hasPermission('CUSTOMER_CREATE')
    const externalCustomersConfig =
      this.workspace.config?.external_customers_config
    const workspaceSupportsCustomers =
      externalCustomersConfig?.enabled &&
      externalCustomersConfig?.allow_requesting_magic_links

    return hasPermission && workspaceSupportsCustomers
  }

  /**
   * Whether to use username/password sign-in type.
   */
  @computed
  get canUseUsernameAndPassword() {
    return this.getCurrentMember().hasPermission('ADMIN_USER_CREATE')
  }

  /**
   * Whether invited clients can be assigned an advisor in this workspace.
   */
  @computed
  get clientAssignmentEnabled() {
    return !!this.workspace.config?.external_customers_config
      ?.client_assignment_enabled
  }

  /**
   * Whether every client this member invites is assigned to them.
   */
  @computed
  get assignsToSelf() {
    const member = this.getCurrentMember()
    return (
      this.clientAssignmentEnabled &&
      member.hasPermission('CLIENT_ASSIGNMENT_MANAGE') &&
      !member.hasPermission('WORKSPACE_CLIENT_ASSIGNMENT_MANAGE')
    )
  }

  /**
   * Whether the current member can choose which advisor to assign.
   */
  @computed
  get canChooseAdvisor() {
    const member = this.getCurrentMember()
    return (
      this.clientAssignmentEnabled &&
      member.hasPermission('WORKSPACE_CLIENT_ASSIGNMENT_MANAGE')
    )
  }

  /**
   * The advisor to assign invited clients to, if any.
   */
  @computed
  get assignedAdvisorUserPublicId() {
    if (this.assignsToSelf) {
      return Id.assert(this.getCurrentMember().user.publicId)
    }
    const advisorId = this.advisorSelect.selected?.user.publicId
    return advisorId ? Id.assert(advisorId) : undefined
  }

  /**
   * Get the list of valid bulk customers (no validation errors).
   */
  @computed
  get validBulkCustomers() {
    return this.bulkCustomers.filter((c) => c.isValid)
  }

  /**
   * Get successful results from bulk submission.
   */
  @computed
  get successfulBulkResults() {
    return this.bulkSubmissionResults.filter((r) => r.success)
  }

  /**
   * Get failed results from bulk submission.
   */
  @computed
  get failedBulkResults() {
    return this.bulkSubmissionResults.filter((r) => !r.success)
  }

  /**
   * Set the email addresses text area.
   */
  @action.bound
  setEmailAddressTextArea(emails: string) {
    this.emailAddressTextArea = emails
  }

  /**
   * Set the sign-in type.
   */
  @action.bound
  setSignInType(type: SignInType) {
    this.signInType = type
  }

  /**
   * Pages members who can be chosen as an advisor.
   * Uses member search so platform-role permissions are included.
   */
  searchAdvisors = (
    params: { limit?: number; after?: string | null; search_text?: string },
    cancellationToken = CancellationToken.default,
  ) =>
    this.memberStore.searchByPermission(
      {
        workspace_id: Id.assert(this.workspace.id),
        user_types: [UserDataType.User],
        has_permission: 'CLIENT_ASSIGNMENT_MANAGE',
        search_text: params.search_text,
        limit: params.limit,
        after: params.after,
      },
      cancellationToken,
    )

  /**
   * Set the customer given name.
   */
  @action.bound
  setCustomerGivenName(name: string) {
    this.customerGivenName = name
  }

  /**
   * Set the customer family name.
   */
  @action.bound
  setCustomerFamilyName(name: string) {
    this.customerFamilyName = name
  }

  /**
   * Set the customer phone number.
   */
  @action.bound
  setCustomerPhoneNumber(phone: string) {
    this.customerPhoneNumber = phone
  }

  /**
   * Set the customer email.
   */
  @action.bound
  setCustomerEmail(email: string) {
    this.customerEmail = email
  }

  /**
   * Set bulk upload mode.
   */
  @action.bound
  setBulkUploadMode(mode: boolean) {
    this.bulkUploadMode = mode
    if (!mode) {
      this.clearBulkUpload()
    }
  }

  /**
   * Clear bulk upload state.
   */
  @action.bound
  clearBulkUpload() {
    this.bulkCustomers = []
    this.bulkUploadError = ''
    this.bulkUploadFileName = ''
    this.bulkSubmissionResults = []
    this.showBulkResults = false
  }

  /**
   * Dismiss the bulk results view and optionally retry failed entries.
   */
  @action.bound
  dismissBulkResults(retryFailed = false) {
    if (retryFailed && this.failedBulkResults.length > 0) {
      // Keep failed entries in the customer list for retry
      this.bulkCustomers = this.bulkCustomers.filter((c) =>
        this.failedBulkResults.some((f) => f.email === c.email),
      )
      this.showBulkResults = false
      this.bulkSubmissionResults = []
    } else {
      this.close()
    }
  }

  /**
   * Handle CSV file upload and parse its contents.
   */
  @action.bound
  async handleCsvUpload(file: File) {
    this.bulkUploadError = ''
    this.bulkUploadFileName = file.name

    try {
      const result = await parseCsvFile(file)

      if (result.headerError) {
        this.bulkUploadError = result.headerError
        this.bulkCustomers = []
        return
      }

      this.bulkCustomers = result.rows
    } catch (err) {
      this.bulkUploadError = 'Failed to read CSV file'
      this.bulkCustomers = []
    }
  }

  /**
   * Remove a customer from the bulk upload list.
   */
  @action.bound
  removeBulkCustomer(index: number) {
    this.bulkCustomers.splice(index, 1)
  }

  /**
   * Initializes the dialog based on available permissions.
   */
  @action.bound
  initialize() {
    if (this.canUseAccessLink && !this.canUseUsernameAndPassword) {
      this.setSignInType('AccessLink')
      return
    }

    this.setSignInType('UsernamePassword')
  }

  /**
   * Opens the dialog.
   */
  @override
  show() {
    this.initialize()
    super.show()
  }

  /**
   * Clears client invitation dialog state.
   */
  @action.bound
  reset() {
    this.setEmailAddressTextArea('')
    this.setSignInType('UsernamePassword')
    this.setCustomerGivenName('')
    this.setCustomerFamilyName('')
    this.setCustomerPhoneNumber('')
    this.setCustomerEmail('')
    this.setBulkUploadMode(false)
    this.advisorSelect.reset()

    this.customerValidation.reset()
    this.invitationValidation.reset()
  }

  /**
   * Send regular workspace invitations to the specified users.
   */
  private async submitInvitations(): Promise<void> {
    if (!this.invitationValidation.reset().validate().isValid) {
      return
    }

    const results: InviteResults[] = await Promise.all(
      this.emailAddresses.map((e) =>
        this.memberStore
          .createManually({
            email: e,
            workspace_id: Id.assert(this.workspace.id),
            // Don't send role_id - let backend assign default
            role_id: undefined,
            skill_ids: [],
            team_id: undefined,
            team_role_id: undefined,
            source: UserSource.Local,
            portal: DtoPortal.CustomerPortal,
          })
          .then(() => {
            return { email: e, success: true }
          })
          .catch((err) => {
            return { email: e, error: err }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // If all invitations succeeded, close dialog.
    if (failed.length < 1) {
      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Invitations sent to ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any invitations failed, populate email text area with the failed email addresses.
    this.newAlert()
      .withState(AlertState.Error)
      .saying(
        `Failed to send invitations to ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    this.setEmailAddressTextArea(failed.map((f) => f.email).join(', '))
  }

  /**
   * Send customer invitation (single mode).
   */
  private async submitSingleCustomer(): Promise<void> {
    if (!this.customerValidation.reset().validate().isValid) {
      return
    }

    try {
      await this.customerStore.create({
        workspace_id: Id.assert(this.workspace.id),
        email: this.customerEmail,
        given_name: this.customerGivenName,
        family_name: this.customerFamilyName,
        phone_number: this.customerPhoneNumber || undefined,
        advisor_user_public_id: this.assignedAdvisorUserPublicId,
      })

      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Customer invitation sent to ${this.customerEmail}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
    } catch (err) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          `Failed to send customer invitation to ${this.customerEmail}.`,
          extractMessageFromError(err),
        )
        .autoDismiss(2500)
        .show()
    }
  }

  /**
   * Send customer invitations (bulk mode).
   * Processes customers in batches of 25 with a delay between batches to avoid rate limiting.
   */
  private async submitBulkCustomers(): Promise<void> {
    const customers = this.validBulkCustomers
    if (customers.length === 0) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('No valid customers to create.')
        .autoDismiss(2500)
        .show()
      return
    }

    const results = await bulkCreateCustomers(
      customers,
      this.customerStore,
      Id.assert(this.workspace.id),
      this.assignedAdvisorUserPublicId,
    )

    // Store results and show results view
    this.bulkSubmissionResults = results
    this.showBulkResults = true
  }

  /**
   * Send customer invitation (dispatches to single or bulk mode).
   */
  private async submitCustomer(): Promise<void> {
    if (this.bulkUploadMode) {
      return this.submitBulkCustomers()
    }
    return this.submitSingleCustomer()
  }

  /**
   * Submit the form based on the selected invitation type.
   */
  submit = task.resolved(async () => {
    return match(this.signInType, {
      UsernamePassword: () => this.submitInvitations(),
      AccessLink: () => this.submitCustomer(),
    })
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    this.reset()
    super.close()
  }
}
